import { BET, BETS, NIGHTMARES, type BetDef, type BetId, type NightmareType } from '@somnhold/content';
import type { BattleParams } from '../battle/create.ts';
import { idiv, imax, imin, mulBp } from '../math/fixed.ts';
import { deriveSeed, rngChance, rngInt, seedRng } from '../math/rng.ts';
import { snapshot } from '../math/hash.ts';
import { hexDist, hexRing } from '../hex.ts';
import type { Spawn } from '../types.ts';
import { event, findPlayer } from './core.ts';
import { groupLevel } from './eras.ts';
import { grantXp } from './progress.ts';
import { descent, field, party, type SagaResult, type SagaTicket } from './saga.ts';
import type { CampaignState, Ms } from './types.ts';

/**
 * Brann and Hilde's bets (docs/SAGAS.md): a fight on a clearing that exists, with one rule changed. Issued,
 * re-simulated and applied like a saga battle act (its ticket carries `bet`); a win adds the bet to the
 * ones won (a barrel in the bastion). Any order, as often as wanted, from the group's level.
 */

export const betDef = (id: BetId): BetDef | undefined => BETS.find((b) => b.id === id);

export const betsOf = (s: CampaignState): NonNullable<CampaignState['bets']> => s.bets ?? { won: [], tries: {} };

/** A bet can be played: the saga is open and the group has its level. */
export const betOpen = (s: CampaignState, id: BetId): boolean => {
  const b = betDef(id);
  return !!b && s.status !== 'lost' && groupLevel(s, s.clock) >= b.minGroup;
};

/** The bets won that put a barrel in the bastion: all but the open gate, which pays a plaque. */
export const barrelBets = (s: CampaignState): string[] => betsOf(s).won.filter((id) => betDef(id as BetId)?.favor !== 'both');

/** The barrels standing in the bastion (one per bet won, but the open gate). */
export const barrelsWon = (s: CampaignState): number => barrelBets(s).length;

/** The fight for a bet: the act on a clearing, then its rule. */
export const betBattleParams = (s: CampaignState, pids: string[], id: BetId): { params: BattleParams; ticket: SagaTicket } | null => {
  const b = betDef(id);
  const who = party(s, pids);
  if (!b || !who.length || !betOpen(s, id)) return null;
  const tries = betsOf(s).tries[id] ?? 0;
  const seed = deriveSeed(s.seed, 70000 + BETS.indexOf(b) * 4096 + (tries % 512));
  const p = b.battle.mode === 'descent' ? descent(s, b.battle, who, seed, BET.race.toughBp) : field(s, b.battle, who, seed, 10000);
  const rules = (p.rules ??= {});
  rules.bet = id;
  if (id === 'towers') rules.turrets = { ...(rules.turrets ?? { ticks: 0, hp: 0, damage: 0 }), max: 0 };
  if (id === 'dark') {
    rules.dark = BET.lantern;
    // In the dark nothing shoots from afar: what can't be lit can't be fought.
    p.spawns = p.spawns.filter((sp) => !(sp.kind in NIGHTMARES) || !(NIGHTMARES[sp.kind as NightmareType].lobs || NIGHTMARES[sp.kind as NightmareType].shootsTowers || NIGHTMARES[sp.kind as NightmareType].shootsHeroes || NIGHTMARES[sp.kind as NightmareType].siege));
  }
  if (id === 'hundred') hundred(p, seed, who.length);
  if (id === 'unscathed') unscathed(p);
  if (id === 'alone') alone(p, seed);
  if (id === 'beside') beside(p);
  // The race: the Warden felled before the clock runs out.
  if (id === 'gate') openGate(p, seed);
  if (id === 'race') rules.race = { limit: BET.race.limit, brann: BET.race.brann };
  return { params: snapshot(p), ticket: { chapter: 0, act: 0, bet: id } };
};

/** A hundred at a blow: goblin packs from every road at once, and the hold lasts a set time. */
const hundred = (p: BattleParams, seed: number, n: number): void => {
  const rng = seedRng(seed ^ 0x100);
  const k = BET.packs;
  const roads = p.map.sectors.length;
  const spawns: Spawn[] = [];
  const size = mulBp(k.size, 10000 + (imax(1, n) - 1) * 2500);
  for (let t = k.first; t < BET.hundredTicks - k.every; t += k.every) {
    for (let i = 0; i < size; i++) spawns.push({ tick: t + idiv(i, roads), sector: i % roads, kind: 'goblin', tier: rngChance(rng, k.tierBp) ? 2 : 1, elite: false });
  }
  // A few bigger ones in the packs, so not every blow lands on goblins alone.
  for (let t = k.first + k.every; t < BET.hundredTicks - k.every; t += k.every * 2) spawns.push({ tick: t, sector: rngInt(rng, roads), kind: 'skeleton', tier: 1, elite: false });
  spawns.sort((a, b) => a.tick - b.tick || a.sector - b.sector);
  p.spawns = spawns;
  p.objectiveTicks = BET.hundredTicks;
  delete p.objectiveQuietRate;
  // The clock is the bet: it doesn't wait for the field to be cleared.
  if (p.rules) delete p.rules.clearToFinish;
  (p.rules ??= {}).burst = { ...BET.burst };
  // The trick is letting them bunch up: the node holds out long enough to try it.
  p.keepHp = p.keepMaxHp = mulBp(p.keepMaxHp, BET.nodeBp);
};

/**
 * Not a scratch: a stone ring close around the node (the heroes climb over it). Nothing throws stones at it from
 * afar, each third of the fight comes down a single road (the group can meet it there), and the first a while later.
 */
const unscathed = (p: BattleParams): void => {
  const { level, material, radius, lead } = BET.ring;
  p.structures = hexRing(radius).map((h) => ({ type: 'wall' as const, level, material, q: h.q, r: h.r }));
  const third = idiv(p.objectiveTicks ?? 1, 3) || 1;
  const roads = p.map.sectors.length;
  p.spawns = p.spawns
    .filter((sp) => !(sp.kind in NIGHTMARES) || !NIGHTMARES[sp.kind as NightmareType].lobs)
    .map((sp) => ({ ...sp, sector: imin(2, idiv(sp.tick, third)) % roads, tick: sp.tick + lead }));
  (p.rules ??= {}).unscathed = true;
};

/**
 * One alone: everything down one road (nothing flies over it or throws from afar), no turrets; the stakes at its mouth
 * count what gets by.
 */
const alone = (p: BattleParams, seed: number): void => {
  const road = rngInt(seedRng(seed ^ 0xa1), p.map.sectors.length);
  p.spawns = p.spawns
    .filter((sp) => !(sp.kind in NIGHTMARES) || !(NIGHTMARES[sp.kind as NightmareType].flying || NIGHTMARES[sp.kind as NightmareType].lobs))
    .map((sp) => ({ ...sp, sector: road, tick: sp.tick + BET.alone.lead }));
  const rules = (p.rules ??= {});
  rules.turrets = { ...(rules.turrets ?? { ticks: 0, hp: 0, damage: 0 }), max: 0 };
  rules.alone = { road, max: BET.alone.max };
};

/**
 * Beside: Hilde's towers in pairs round the node, off the roads (the two of a pair side by side, so one hero can stand
 * by both); a tower shoots only with a standing hero by it. No turrets.
 */
const beside = (p: BattleParams): void => {
  const k = BET.beside;
  const onPath = new Set(p.map.sectors.flatMap((sec) => sec.path.map((h) => `${h.q},${h.r}`)));
  const ring = hexRing(k.ring);
  const n = ring.length;
  const free = (i: number): boolean => !onPath.has(`${ring[i % n]?.q},${ring[i % n]?.r}`);
  p.structures = [];
  for (let j = 0; j < k.pairs; j++) {
    let i = idiv(j * n, k.pairs);
    for (let t = 0; t < n && !(free(i) && free(i + 1)); t++) i++;
    for (let m = 0; m < 2; m++) {
      const h = ring[(i + m) % n] as { q: number; r: number };
      p.structures.push({ type: k.types[m % k.types.length] as (typeof k.types)[number], level: k.level, material: k.material, q: h.q, r: h.r });
    }
  }
  const rules = (p.rules ??= {});
  rules.turrets = { ...(rules.turrets ?? { ticks: 0, hp: 0, damage: 0 }), max: 0 };
  rules.beside = k.reach;
};

/**
 * The open gate: Brann's stone ring round the node, a wall on every hex of it but one: the gate, open, where one road
 * comes into the clearing. Whatever comes down the other roads walks round the outside to it (or breaks the wall). No turrets.
 */
const openGate = (p: BattleParams, seed: number): void => {
  const k = BET.gate;
  const road = rngInt(seedRng(seed ^ 0x9a7e), p.map.sectors.length);
  const ring = hexRing(k.radius);
  // The gate faces the road: the hex of the ring nearest where it comes into the clearing.
  const mouth = p.map.sectors[road]?.gate ?? { q: p.map.bastion, r: 0 };
  const gate = ring.slice().sort((a, b) => hexDist(a, mouth) - hexDist(b, mouth) || a.q - b.q || a.r - b.r)[0] ?? { q: k.radius, r: 0 };
  p.structures = ring.filter((h) => h.q !== gate.q || h.r !== gate.r).map((h) => ({ type: 'wall' as const, level: k.level, material: k.material, q: h.q, r: h.r }));
  p.spawns = p.spawns.map((sp) => ({ ...sp, tick: sp.tick + k.lead }));
  const rules = (p.rules ??= {});
  rules.turrets = { ...(rules.turrets ?? { ticks: 0, hp: 0, damage: 0 }), max: 0 };
  rules.openGate = { q: gate.q, r: gate.r };
};

/** A bet fought: a win puts its barrel in the bastion (once) and pays XP (half after the first). */
export const applyBet = (s: CampaignState, who: string[], id: BetId, won: boolean, now: Ms): SagaResult => {
  const bets = (s.bets ??= { won: [], tries: {} });
  bets.tries[id] = (bets.tries[id] ?? 0) + 1;
  const first = won && !bets.won.includes(id);
  if (won) for (const pid of who) if (findPlayer(s, pid)) grantXp(s, pid, first ? BET.xp : idiv(BET.xp, 2), now);
  if (first) bets.won.push(id);
  // The open gate: Hilde's plaque on the bastion's gate, with the names of the heroes who held it (the first time).
  if (first && id === 'gate') bets.plaque = who.filter((pid) => findPlayer(s, pid));
  event(s, now, 'bet', { pid: who[0] ?? '', data: { bet: id, won, first, party: who.join(','), payer: betDef(id)?.favor === 'brann' ? 'hilde' : betDef(id)?.favor === 'both' ? '' : 'brann' } });
  return { counted: true, won };
};
